'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users } from 'lucide-react';

import { MetricCard } from './MetricCard';
import { EmptyState } from './EmptyState';
import { ChartContainer } from './ChartContainer';
import { useCustomerConversionFunnelData } from '@/lib/api/analytics';
import { useStatsFilters } from '../context/StatsFiltersContext';

interface CustomerConversionFunnelProps {
    className?: string;
}

export function CustomerConversionFunnel({ className = '' }: CustomerConversionFunnelProps) {
    const { selectedDates, selectedStores, selectedCurrency } = useStatsFilters();
    const { t } = useTranslation('analytics');

    const { data, isLoading, error } = useCustomerConversionFunnelData(
        {
            startDate: selectedDates.from!,
            endDate: selectedDates.to!,
            storeIds: selectedStores,
            currency: selectedCurrency,
        },
        {
            enabled: !!(selectedDates.from && selectedDates.to),
        }
    );

    if (error) {
        return (
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${className}`}>
                <MetricCard title={t('conversion.funnel.title')} description={t('conversion.funnel.description')}>
                    <div className="flex items-center justify-center h-full">
                        <p className="text-destructive">{t('conversion.funnel.errorMessage')}</p>
                    </div>
                </MetricCard>
            </div>
        );
    }

    const steps = data?.steps || [];
    const hasData = steps.length > 0 && steps[0].value > 0;

    // Transform data for bar chart
    const chartData = steps.map((step) => ({
        code: step.label,
        value: step.value,
    }));

    return (
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${className}`}>
            {/* Funnel Bar Chart */}
            <MetricCard
                title={t('conversion.funnel.title')}
                description={t('conversion.funnel.description')}
                isLoading={isLoading}
            >
                {!hasData ? (
                    <EmptyState message={t('conversion.funnel.emptyState')} />
                ) : (
                    <ChartContainer
                        data={chartData}
                        chartType="bar"
                        emptyStateMessage={t('conversion.funnel.breakdown.emptyState')}
                    />
                )}
            </MetricCard>

            {/* Funnel Steps Detail */}
            <MetricCard
                title={t('conversion.funnel.breakdown.title')}
                description={t('conversion.funnel.breakdown.description')}
                isLoading={isLoading}
                dynamicHeight
            >
                {!hasData ? (
                    <EmptyState message={t('conversion.funnel.breakdown.emptyState')} />
                ) : (
                    <>
                        <div className="space-y-3">
                            {steps.map((step, index) => (
                                <div key={step.code} className="flex items-center gap-3">
                                    {/* Step indicator */}
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <span className="text-sm font-medium text-primary">{index + 1}</span>
                                    </div>

                                    {/* Step details */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-foreground truncate">{step.label}</span>
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-1">
                                                    <Users className="h-3 w-3 text-muted-foreground" />
                                                    <span className="text-sm text-muted-foreground">{step.value.toLocaleString()}</span>
                                                </div>
                                                <span className="text-sm font-medium text-foreground w-16 text-right">
                                                    {step.conversionOverall.toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>

                                        {/* Progress bar */}
                                        <div className="mt-1 h-2 bg-muted rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-primary transition-all duration-500"
                                                style={{ width: `${step.conversionOverall}%` }}
                                            />
                                        </div>

                                        {/* Step-to-step conversion */}
                                        {index > 0 && (
                                            <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                                                <ArrowRight className="h-3 w-3" />
                                                <span>{t('conversion.funnel.fromPrevious', { rate: step.conversionFromPrevious.toFixed(1) })}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Overall conversion summary */}
                        {steps.length >= 2 && (
                            <div className="mt-4 pt-4 border-t border-border">
                                <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                                    <span className="text-sm font-medium text-foreground">{t('conversion.funnel.overallRate')}</span>
                                    <span className="text-lg font-semibold text-primary">
                                        {steps[steps.length - 1].conversionOverall.toFixed(1)}%
                                    </span>
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground">
                                    {t('conversion.funnel.completedCheckout', { completed: steps[steps.length - 1].value.toLocaleString(), total: steps[0].value.toLocaleString() })}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </MetricCard>
        </div>
    );
}
